import React from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import LandingProfielen from './LandingProfielen';
import WorkspacePage from './pages/WorkspacePage';
import PrivateRoute from './PrivateRoute';
import AccountManagement from './pages/AccountManagement';
import PaymentSuccess from './pages/PaymentSuccess';
import SignupExisting from './pages/SignupExisting';
import AuthentikCallback from './components/auth/AuthentikCallback';
import * as authentikOidc from './auth/authentikOidc';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import './PrivateRoute.css'; // Shared access page styling

/**
 * AutoLogin, handles ?auto_login=true from chat.humanaize.nl SSO flow.
 * When a user clicks the Matcher in the chat.humanaize Marketplace, they arrive
 * here. We trigger an Authentik PKCE redirect; if the user already has an
 * Authentik session it resolves silently (no login screen).
 */
function AutoLogin() {
  const attempted = React.useRef(false);

  React.useEffect(() => {
    if (attempted.current) return;
    if (authentikOidc.isAuthenticated()) return;
    attempted.current = true;
    authentikOidc.login({ returnTo: '/app' });
  }, []);

  if (authentikOidc.isAuthenticated()) {
    return null; // App component will handle routing
  }

  return (
    <div className="access-loading">
      <span className="access-loading__icon">🔐</span>
      <p className="access-loading__text">Inloggen via Humanaize...</p>
    </div>
  );
}

function App() {
  // AIVD demo: no auth, ignore ?auto_login=true silently.

  return (
    <SubscriptionProvider>
      {/* Toast notifications - placed at app level */}
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#363636',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />

      <Routes>
        {/* Public routes */}
        {/* AIVD matcher pivot, 2026-05-27:
            `/` is now LandingProfielen (showcase of Humanaize engineers).
            Old landing components removed; legacy paths just redirect home. */}
        <Route path="/" element={<LandingProfielen />} />
        <Route path="/profielen" element={<LandingProfielen />} />
        {/* Oude landing-paden → redirect naar de detachering-dossier home. */}
        <Route path="/landing-haijob" element={<Navigate to="/" replace />} />
        <Route path="/landing-old" element={<Navigate to="/" replace />} />
        <Route path="/landing-vakature" element={<Navigate to="/" replace />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/signup-existing" element={<SignupExisting />} />

        {/* Authentik OIDC callback (PKCE token exchange) */}
        <Route path="/auth/callback" element={<AuthentikCallback />} />
        {/* Legacy Auth0 callback path: any stale session-cookie redirect lands here.
            Send users to landing instead of a 404. */}
        <Route path="/callback" element={<Navigate to="/" replace />} />

        {/* AIVD demo: no auth gate on /app — direct access. */}
        <Route path="/app" element={<WorkspacePage />} />

        <Route
          path="/subscription/success"
          element={
            <PrivateRoute>
              <div style={{
                padding: '60px 40px',
                textAlign: 'center',
                maxWidth: '600px',
                margin: '0 auto',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '40px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ fontSize: '64px', marginBottom: '24px' }}>✅</div>
                  <h1 style={{ fontSize: '32px', marginBottom: '16px', color: '#1f2937' }}>
                    Welkom bij Humanaize Matcher!
                  </h1>
                  <p style={{ fontSize: '18px', marginBottom: '32px', color: '#6b7280', lineHeight: '1.6' }}>
                    Je 7-daagse gratis proefperiode is gestart. We hebben je een bevestigingsmail gestuurd.
                  </p>
                  <Link
                    to="/app"
                    style={{
                      display: 'inline-block',
                      padding: '14px 32px',
                      backgroundColor: '#0d9488',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    Ga naar de app →
                  </Link>
                </div>
              </div>
            </PrivateRoute>
          }
        />

        <Route
          path="/subscription/cancel"
          element={
            <div className="access-page">
              <div className="access-card">
                <span className="access-icon">❌</span>
                <h1 className="access-title">Checkout Geannuleerd</h1>
                <p className="access-description">
                  Je hebt het checkout proces geannuleerd. Je kunt altijd later nog een abonnement starten.
                </p>
                <div className="access-actions">
                  <Link to="/app" className="access-btn access-btn--primary">
                    🚀 Terug naar de App
                  </Link>
                  <Link to="/" className="access-btn access-btn--secondary">
                    🏠 Naar Homepage
                  </Link>
                </div>
              </div>
            </div>
          }
        />

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="access-page">
              <div className="access-card">
                <span className="access-icon">🔍</span>
                <h1 className="access-title">Pagina Niet Gevonden</h1>
                <p className="access-description">
                  Deze pagina bestaat niet of is verplaatst.
                </p>
                <div className="access-actions">
                  <Link to="/" className="access-btn access-btn--primary">
                    🏠 Terug naar Home
                  </Link>
                </div>
              </div>
            </div>
          }
        />
      </Routes>
    </SubscriptionProvider>
  );
}

export default App;
